import { useState, useCallback, useEffect, useRef } from 'react'
import Map, { Layer, Source, MapLayerMouseEvent, MapRef } from 'react-map-gl'
import { Loader } from '@mantine/core'
import 'mapbox-gl/dist/mapbox-gl.css'

type Point = [number, number]

interface Override {
  type: 'Feature'
  id: string
  properties: { name: string; region?: string }
  geometry: { type: string; coordinates: number[][][] }
}

interface HealthRegion {
  name: string
  bbox: [number, number, number, number]
  ready: boolean
}

export default function SeaOverridesPage() {
  const [overrides, setOverrides] = useState<Override[]>([])
  const [drawing, setDrawing] = useState<Point[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'view' | 'draw'>('view')
  const [regions, setRegions] = useState<HealthRegion[]>([])
  const mapRef = useRef<MapRef>(null)

  useEffect(() => {
    fetch('/routing/health')
      .then((res) => res.json())
      .then((data) => setRegions(data.regions ?? []))
      .catch(() => {})
  }, [])

  const fetchOverrides = useCallback(async () => {
    try {
      const res = await fetch('/routing/sea-overrides')
      const data = await res.json()
      setOverrides(data.overrides ?? [])
    } catch (err: any) {
      setError(err.message)
    }
  }, [])

  useEffect(() => {
    fetchOverrides()
  }, [fetchOverrides])

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (mode !== 'draw') return
      const point: Point = [e.lngLat.lng, e.lngLat.lat]
      setDrawing((prev) => [...prev, point])
    },
    [mode]
  )

  const handleDoubleClick = useCallback(
    async (e: MapLayerMouseEvent) => {
      if (mode !== 'draw' || drawing.length < 3) return
      e.preventDefault()

      const polygon = [...drawing, drawing[0]]
      setLoading(true)
      setError(null)

      try {
        const res = await fetch('/routing/sea-overrides', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            geometry: {
              type: 'Polygon',
              coordinates: [polygon],
            },
          }),
        })
        if (!res.ok) {
          const body = await res
            .json()
            .catch(() => ({ detail: res.statusText }))
          throw new Error(body.detail || res.statusText)
        }
        await fetchOverrides()
        setDrawing([])
        setMode('view')
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [mode, drawing, fetchOverrides]
  )

  const handleDelete = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        const res = await fetch(`/routing/sea-overrides/${id}`, {
          method: 'DELETE',
        })
        if (!res.ok) {
          const body = await res
            .json()
            .catch(() => ({ detail: res.statusText }))
          throw new Error(body.detail || res.statusText)
        }
        await fetchOverrides()
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [fetchOverrides]
  )

  const overridesGeojson = {
    type: 'FeatureCollection' as const,
    features: overrides,
  }

  const drawingGeojson =
    drawing.length > 0
      ? {
          type: 'FeatureCollection' as const,
          features: [
            // Line showing current drawing progress
            {
              type: 'Feature' as const,
              geometry: {
                type: 'LineString' as const,
                coordinates: drawing,
              },
              properties: {},
            },
            // Points for each vertex
            ...drawing.map((p, i) => ({
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: p },
              properties: { index: i },
            })),
          ],
        }
      : null

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawing([])
        setMode('view')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const statusText =
    mode === 'draw'
      ? drawing.length < 3
        ? `Click to add vertices (${drawing.length}/3 min). Press Esc to cancel.`
        : `${drawing.length} vertices. Double-click to finish. Press Esc to cancel.`
      : `${overrides.length} override zone${overrides.length !== 1 ? 's' : ''}. Click "Draw" to add.`

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken="pk.eyJ1IjoiaGJhbmRvdiIsImEiOiJjbHVoMTZqazEya2p5MnNsMHF1c3ZicmV6In0.wcM_OYGTxxDZq8D8tnfKxA"
        initialViewState={{
          latitude: 43,
          longitude: 15.5,
          zoom: 6,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v9"
        attributionControl={false}
        onClick={handleClick}
        onDblClick={handleDoubleClick}
        doubleClickZoom={mode !== 'draw'}
        cursor={mode === 'draw' ? 'crosshair' : 'grab'}
      >
        {/* Existing override polygons */}
        <Source id="overrides-source" type="geojson" data={overridesGeojson}>
          <Layer
            id="overrides-fill"
            type="fill"
            paint={{
              'fill-color': '#3b82f6',
              'fill-opacity': 0.25,
            }}
          />
          <Layer
            id="overrides-outline"
            type="line"
            paint={{
              'line-color': '#2563eb',
              'line-width': 2,
            }}
          />
        </Source>

        {/* Drawing in progress */}
        {drawingGeojson && (
          <Source id="drawing-source" type="geojson" data={drawingGeojson}>
            <Layer
              id="drawing-line"
              type="line"
              filter={['==', '$type', 'LineString']}
              paint={{
                'line-color': '#f59e0b',
                'line-width': 2,
                'line-dasharray': [2, 2],
              }}
            />
            <Layer
              id="drawing-points"
              type="circle"
              filter={['==', '$type', 'Point']}
              paint={{
                'circle-radius': 5,
                'circle-color': '#f59e0b',
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 2,
              }}
            />
          </Source>
        )}
      </Map>

      {/* Status bar */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '8px 16px',
          borderRadius: 8,
          fontSize: 14,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {loading && <Loader size="xs" />}
        <span style={{ color: error ? '#e03131' : '#333' }}>
          {error ? `Error: ${error}` : statusText}
        </span>
      </div>

      {/* Action buttons */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          gap: 8,
        }}
      >
        {regions.length > 1 &&
          regions.map((region) => (
            <button
              key={region.name}
              onClick={() =>
                mapRef.current?.fitBounds(
                  [
                    [region.bbox[0], region.bbox[1]],
                    [region.bbox[2], region.bbox[3]],
                  ],
                  { padding: 40, duration: 500 }
                )
              }
              title={region.ready ? undefined : 'mesh building…'}
              style={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 14,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                opacity: region.ready ? 1 : 0.5,
              }}
            >
              {region.name}
            </button>
          ))}
        {mode === 'view' ? (
          <button
            onClick={() => setMode('draw')}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            Draw Override Zone
          </button>
        ) : (
          <button
            onClick={() => {
              setDrawing([])
              setMode('view')
            }}
            style={{
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Override list panel */}
      {overrides.length > 0 && mode === 'view' && (
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 8,
            padding: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            maxHeight: '40vh',
            overflowY: 'auto',
            minWidth: 250,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
            Override Zones
          </div>
          {overrides.map((o) => (
            <div
              key={o.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 0',
                borderBottom: '1px solid #eee',
                fontSize: 13,
              }}
            >
              <span>
                {o.properties.name || o.id.slice(0, 8)}
                {o.properties.region ? ` · ${o.properties.region}` : ''}
              </span>
              <button
                onClick={() => handleDelete(o.id)}
                disabled={loading}
                style={{
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: 4,
                  padding: '2px 8px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
